import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import Alert from '@material-ui/lab/Alert';
import get from 'lodash/get';
import strings from 'strings';
import { TextBox } from 'UI/components/atoms';
import FPHint from 'UI/components/atoms/FPHint';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import FPTextEditor from 'UI/components/molecules/FPTextEditor';
import { BulkAIButton } from 'UI/components/organisms/BulkEmailDrawer/AI/BulkAIButton/BulkAIButton';
import { BulkAIGeneratorDrawer } from 'UI/components/organisms/BulkEmailDrawer/AI/BulkAIGeneratorDrawer/BulkAIGeneratorDrawer';
import { useBulkAIGenerator } from 'UI/components/organisms/BulkEmailDrawer/AI/BulkAIGeneratorDrawer/BulkAIGeneratorDrawer.hooks';
import { useBulkSmartTags } from 'UI/components/organisms/BulkEmailDrawer/BulkEmailDrawerSmartTags.hook';
import { BulkFormFieldGrid } from 'UI/components/organisms/BulkEmailForm/components/BulkFormFieldGrid';
import { BulkDrawerFormFieldsMap } from 'UI/components/organisms/BulkEmailForm/fields';
import { tinymceBulkConfig } from 'UI/constants/config';
import { idOptionSelected } from 'UI/utils';
import { LabelRenderers } from 'UI/utils/renderers';

import { buildAttemptsFormKey } from '../../BulkSequences.utils';

const DEFAULT_TRIGGER_DAYS_LENGTH = 30;

const buildOption = ({ day, recommendedTriggerDay, scopeTitle, currentStep }) => {
  if (!day) return null;

  const recommendedSuffix =
    recommendedTriggerDay === day && scopeTitle
      ? strings.formatString(sequencesCopies.sections.triggerDays.options.recommendedSuffix, {
          bulkScope: scopeTitle,
          step: currentStep
        })
      : '';
  const dayLabel = day > 1 ? 'Days' : 'Day';
  const finalTitle = strings.formatString(sequencesCopies.sections.triggerDays.options.title, {
    day,
    dayLabel,
    recommendedSuffix
  });

  return {
    id: day,
    title: finalTitle,
    value: day
  };
};

const {
  sequences: sequencesCopies,
  fields: fieldsCopies,
  signatureInfo
} = strings.bulkEmails.forms;

export const BulkAttemptForm = ({
  onFetchingSmartTags = () => {},
  recommendedTriggerDays,
  scopeTitle,
  step
}) => {
  const editorRef = useRef(null);
  const prevScope = useRef(scopeTitle);

  const paths = useMemo(() => {
    const baseFormKey = buildAttemptsFormKey(step.id);
    return {
      emailBody: `${baseFormKey}.emailBody`,
      promptId: `${baseFormKey}.promptId`,
      subject: `${baseFormKey}.subject`,
      triggerDays: `${baseFormKey}.triggerDays`
    };
  }, [step.id]);

  const { errors, getValues, register, setValue, unregister, watch } = useFormContext();
  const watchers = watch([paths.emailBody, paths.subject, paths.triggerDays]);
  const smartTagsWatcher = watch(BulkDrawerFormFieldsMap.SmartTags.key);
  const triggerDaysWatcher = watchers[paths.triggerDays];
  const recommendedTriggerDay =
    recommendedTriggerDays[step.currentStep] ?? recommendedTriggerDays?.default;

  const onSetEmailContent = useCallback(
    ({ emailBody, promptId, subject }) => {
      register({ name: paths.promptId, value: promptId });
      setValue(paths.subject, subject, true);
      setValue(paths.emailBody, emailBody, true);
    },
    [paths, register, setValue]
  );
  const {
    isAIDrawerOpen,
    shouldShowAIButton,
    shouldEnableAIAction,
    handleOpenAIDrawer,
    drawerProps: bulkAIDrawerProps
  } = useBulkAIGenerator({ emailBodyKey: paths.emailBody, onSetEmailContent });

  const commonTriggerOptions = useMemo(
    () => ({
      recommendedTriggerDay,
      scopeTitle,
      currentStep: step.currentStep
    }),
    [recommendedTriggerDay, scopeTitle, step.currentStep]
  );

  const triggerDaysOptions = useMemo(
    () =>
      Array.from({ length: DEFAULT_TRIGGER_DAYS_LENGTH }, (_, index) => {
        const day = index + 1;
        return buildOption({
          day,
          ...commonTriggerOptions
        });
      }),
    [commonTriggerOptions]
  );

  const finalSelectedTriggerDay = useMemo(() => {
    if (triggerDaysWatcher) {
      const updatedOption = buildOption({
        day: triggerDaysWatcher.value,
        ...commonTriggerOptions
      });

      if (triggerDaysWatcher.title !== updatedOption.title) {
        return updatedOption;
      }

      return triggerDaysWatcher;
    }

    return null;
  }, [commonTriggerOptions, triggerDaysWatcher]);

  useBulkSmartTags({
    getValues,
    onFetchingSmartTags,
    register,
    registrationConfig: {
      subject: {
        key: paths.subject,
        value: step.subject || '',
        requiredMessage: sequencesCopies.sections.subject.requiredMessage,
        fieldName: 'attempt subject'
      },
      emailBody: {
        key: paths.emailBody,
        value: step.emailBody || null,
        requiredMessage: sequencesCopies.sections.emailBody.requiredMessage,
        fieldName: 'attempt email body'
      }
    },
    setValue
  });

  useEffect(() => {
    const isTriggerDaysFieldUnregistered = typeof triggerDaysWatcher === 'undefined';

    if (isTriggerDaysFieldUnregistered) {
      register(
        { name: paths.triggerDays },
        { required: sequencesCopies.sections.triggerDays.requiredMessage }
      );
      setValue(
        paths.triggerDays,
        buildOption({
          day: step.triggerDays || recommendedTriggerDay,
          ...commonTriggerOptions
        })
      );
    }
  }, [
    commonTriggerOptions,
    paths.triggerDays,
    recommendedTriggerDay,
    register,
    setValue,
    step.triggerDays,
    triggerDaysWatcher
  ]);

  useEffect(() => {
    if (scopeTitle !== prevScope.current) {
      setValue(
        paths.triggerDays,
        buildOption({
          day: recommendedTriggerDay,
          ...commonTriggerOptions
        }),
        true
      );
    }
  }, [commonTriggerOptions, paths.triggerDays, recommendedTriggerDay, scopeTitle, setValue]);

  useEffect(() => {
    return () => {
      unregister([paths.emailBody, paths.promptId, paths.subject, paths.triggerDays]);
    };
  }, [paths, unregister]);

  const handleSubjectChange = (_, value) => {
    setValue(paths.subject, value, true);
  };

  const handleEmailBodyChange = value => {
    setValue(paths.emailBody, value, true);
  };

  const handleSelectTriggerdays = (name, value) => {
    prevScope.current = scopeTitle;
    setValue(name, value, true);
  };

  return (
    <>
      <Alert severity="info">{sequencesCopies.alerts.recipientsToReach}</Alert>
      <BulkFormFieldGrid
        label={sequencesCopies.sections.trigger.label}
        field={sequencesCopies.sections.trigger.field}
        shouldShowField
      />
      <BulkFormFieldGrid
        label={sequencesCopies.sections.triggerDays.label}
        field={
          <AutocompleteSelect
            defaultOptions={triggerDaysOptions}
            error={!!get(errors, paths.triggerDays, false)}
            errorText={get(errors, paths.triggerDays, {})?.message}
            getOptionLabel={option => option?.title || ''}
            getOptionSelected={idOptionSelected}
            name={paths.triggerDays}
            onSelect={handleSelectTriggerdays}
            placeholder={sequencesCopies.sections.triggerDays.placeholder}
            selectedValue={finalSelectedTriggerDay ?? null}
          />
        }
        shouldShowField
      />
      <FPHint variant="warning" description={sequencesCopies.sections.triggerDays.hint} size="lg" />
      {shouldShowAIButton && (
        <BulkAIButton shouldEnable={shouldEnableAIAction} onOpenAiDrawer={handleOpenAIDrawer} />
      )}
      <Alert severity="info">{sequencesCopies.alerts.quota}</Alert>
      <BulkFormFieldGrid
        label={sequencesCopies.sections.subject.label}
        field={
          <TextBox
            name={paths.subject}
            value={get(watchers, paths.subject, '')}
            placeholder={sequencesCopies.sections.subject.label}
            onChange={handleSubjectChange}
            error={!!get(errors, paths.subject, false)}
            errorText={get(errors, paths.subject, {})?.message}
          />
        }
        shouldShowField
      />
      <BulkFormFieldGrid
        label={fieldsCopies.smartTags.label}
        hint={fieldsCopies.smartTags.hint}
        field={
          <AutocompleteSelect
            /**
             * DirtyHack: added props selectedValue=[] and multiple=true in order to avoid
             * autocomplete selected value console warnings and to keep current functionality
             * of selecting an option without setting the currently selected choice as input's
             * value.
             */
            multiple
            selectedValue={[]}
            placeholder={fieldsCopies.smartTags.placeholder}
            placeholderAsLabel={false}
            onSelect={(_, value) => editorRef.current.insertContent(value[0].value)}
            getOptionDisabled={option => option.value === signatureInfo.selectOption.value}
            defaultOptions={smartTagsWatcher}
            getOptionLabel={LabelRenderers.nameOrEmpty}
          />
        }
      />
      <FPTextEditor
        onInit={(e, editor) => {
          editorRef.current = editor;
        }}
        config={tinymceBulkConfig}
        value={get(watchers, paths.emailBody, '')}
        error={!!get(errors, paths.emailBody, false)}
        errorText={get(errors, paths.emailBody, {})?.message}
        onChange={handleEmailBodyChange}
      />
      {isAIDrawerOpen && <BulkAIGeneratorDrawer {...bulkAIDrawerProps} />}
    </>
  );
};
